// Imports ------------------------------------------------------------------->

import { Service, service, inject } from './di'
import { StorageService } from './StorageService'
import { EventService } from './EventService'
import { Flyer } from '../definitions/FlyerDefinitions'
import { ConfigService } from './ConfigService'
import { AccountsUser } from '../definitions/CommonDefinitions'
import { fetchRaw, fetchJSON } from '../utils/fetch'

// Private Definitions ------------------------------------------------------->

export type Favourites = number[]

interface Favorite {
    merchant_id: number
}

interface FavoritesResponse {
    favorites: Favorite[]
}

// Public Definitions -------------------------------------------------------->

@service
export class FavouriteService extends Service {
    @inject storage: StorageService
    @inject config: ConfigService
    @inject eventService: EventService

    private _favourites: Favourites
    private _favouritesToAdd: Favourites = []
    private _favouritesToDelete: Favourites = []

    addFavourite(flyer: Flyer, token: AccountsUser) {
        this._favourites.push(flyer.merchantId)
        if (token) {
            this._batchCreateFavourites([flyer.merchantId], token)
        } else if (
            this._favouritesToAdd.findIndex(
                (item) => item === flyer.merchantId
            ) === -1
        ) {
            this._favouritesToAdd.push(flyer.merchantId)
            const idx = this._favouritesToDelete.findIndex(
                (item) => item === flyer.merchantId
            )
            if (idx !== -1) {
                this._favouritesToDelete.splice(idx, 1)
            }
        }
        this.eventService.fireFavouritesEvent('changed', this._favourites)
    }

    removeFavourite(flyer: Flyer, token: AccountsUser) {
        const idx = this._favourites.indexOf(flyer.merchantId)
        if (idx === -1) {
            return
        }
        if (token) {
            this._batchDeleteFavourites([flyer.merchantId], token)
        } else if (
            this._favouritesToDelete.findIndex(
                (item) => item === flyer.merchantId
            ) === -1
        ) {
            this._favouritesToDelete.push(flyer.merchantId)
            const idx = this._favouritesToAdd.findIndex(
                (item) => item === flyer.merchantId
            )
            if (idx !== -1) {
                this._favouritesToAdd.splice(idx, 1)
            }
        }
        this._favourites.splice(idx, 1)
        this.eventService.fireFavouritesEvent('changed', this._favourites)
    }

    async isFavourite(flyer: Flyer, token: AccountsUser) {
        if (!flyer) {
            return false
        }
        if (!this._favourites) await this._initFavorites(token)
        return this._favourites.indexOf(flyer.merchantId) !== -1
    }

    async getFavouriteMerchantIds(token: AccountsUser) {
        if (!this._favourites) await this._initFavorites(token)
        return this._favourites
    }

    async onLogin(token: AccountsUser) {
        if (this._favouritesToDelete.length) {
            await this._batchDeleteFavourites(this._favouritesToDelete, token)
            this._favouritesToDelete = []
            await this._batchCreateFavourites(this._favouritesToAdd, token)
        }
        this._favouritesToAdd = []
        await this._initFavorites(token)
    }

    private async _initFavorites(token: AccountsUser) {
        this._favourites = []
        try {
            this._favourites = token ? await this._fetchFavourites(token) : []
        } catch (e) {
            console.log(e)
        }
        this.eventService.fireFavouritesEvent('changed', this._favourites)
        return this._favourites
    }

    private _fetchFavourites(token: AccountsUser): Promise<Favourites> {
        return fetchJSON<FavoritesResponse>(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/favorites`,
            {
                method: 'GET',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
            }
        ).then((data) => {
            return data.favorites.map((item: Favorite) => item.merchant_id)
        })
    }

    private _batchCreateFavourites(merchantIds: number[], token: AccountsUser) {
        const body = {
            favorites: merchantIds.map((id) => ({ merchant_id: id })),
        }
        return fetchRaw(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/favorites/batch_create`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    private _batchDeleteFavourites(merchantIds: number[], token: AccountsUser) {
        const body = {
            favorites: merchantIds.map((id) => ({ merchant_id: id })),
        }
        return fetchRaw(
            `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/favorites/batch_delete`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Authorization', `Token token=${token.access_token}`],
                ],
                body: JSON.stringify(body),
            }
        )
    }
}
